import { z } from 'zod';
import type { CalendarMonthEvent } from './calendar-events.js';

/** Birthday calendar (spec §6.13). The year of birth is never stored or sent. */

export interface Birthday {
  readonly month: number;
  readonly day: number;
}

/** A calendar date in the office time zone; `month` is 1-12. */
export interface LocalDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** Longest each month can be in any year (29 Feb is a valid birthday). */
const MAX_DAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return MAX_DAYS[month - 1] ?? 0;
}

export function isValidBirthday(month: number, day: number): boolean {
  return (
    Number.isInteger(month) &&
    Number.isInteger(day) &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= (MAX_DAYS[month - 1] ?? 0)
  );
}

export const birthdaySchema = z
  .object({ month: z.number().int().min(1).max(12), day: z.number().int().min(1).max(31) })
  .strict()
  .refine((b) => isValidBirthday(b.month, b.day), { message: 'No such day in that month' });

/** The day a birthday is celebrated in `year`: 29 Feb moves to 28 Feb in non-leap years. */
export function celebrationDay(birthday: Birthday, year: number): Birthday {
  if (birthday.month === 2 && birthday.day === 29 && !isLeapYear(year)) {
    return { month: 2, day: 28 };
  }
  return birthday;
}

export function isBirthdayOn(birthday: Birthday, date: LocalDate): boolean {
  const day = celebrationDay(birthday, date.year);
  return day.month === date.month && day.day === date.day;
}

/** Today's date in `timeZone` (an IANA name such as `Europe/Stockholm`), DST-aware. */
export function localDate(now: Date, timeZone: string): LocalDate {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return { year: part('year'), month: part('month'), day: part('day') };
}

/** One visible birthday in a month view. `day` is already the celebrated day for that year. */
export interface CalendarBirthday {
  readonly userId: string;
  readonly displayName: string;
  readonly day: number;
}

/** Response of `GET /api/calendar?year=&month=`. */
export interface CalendarMonthResponse {
  readonly year: number;
  readonly month: number;
  readonly birthdays: CalendarBirthday[];
  /** Admin-created company events in the month, sorted by day (spec §6.13). */
  readonly events: CalendarMonthEvent[];
}

export const CALENDAR_YEAR_MIN = 2000;
export const CALENDAR_YEAR_MAX = 2100;

export const calendarQuerySchema = z.object({
  year: z.coerce.number().int().min(CALENDAR_YEAR_MIN).max(CALENDAR_YEAR_MAX),
  month: z.coerce.number().int().min(1).max(12),
});

export interface BirthdayOwner {
  readonly userId: string;
  readonly displayName: string;
  readonly birthday: Birthday;
}

/** The birthdays celebrated in a month, sorted by day, then name. */
export function birthdaysInMonth(
  owners: readonly BirthdayOwner[],
  year: number,
  month: number,
): CalendarBirthday[] {
  return owners
    .map((owner) => ({ owner, on: celebrationDay(owner.birthday, year) }))
    .filter(({ on }) => on.month === month)
    .map(({ owner, on }) => ({
      userId: owner.userId,
      displayName: owner.displayName,
      day: on.day,
    }))
    .sort((a, b) => a.day - b.day || a.displayName.localeCompare(b.displayName));
}
